<template>
  <div class="training-record">
    <!-- 顶部统计卡片 -->
    <div class="card-container">
      <div class="stat-card">
        <div class="card-content">
          <h3>参与培训人数</h3>
          <h2>{{ totalParticipants }}</h2>
          <span class="growth"
            >{{ isPositiveGrowth ? "↑" : "↓" }}较上月{{
              isPositiveGrowth ? "增长" : "减少"
            }}{{ growthPercentage }}%</span
          >
        </div>
      </div>
      <div class="stat-card">
        <div class="card-content">
          <h3>已完成课程</h3>
          <h2>{{ completedCourses }}</h2>
          <span class="status">完成率{{ completionRate }}%</span>
          <div class="progress">
            <div
              class="progress-bar"
              :style="`width: ${completionRate}%`"
            ></div>
          </div>
        </div>
      </div>
      <div class="stat-card">
        <div class="card-content">
          <h3>总学习时长</h3>
          <h2>{{ Math.floor(totalStudyMinutes) }}</h2>
          <span class="unit">分钟</span>
        </div>
      </div>
    </div>

    <!-- 搜索栏 -->
    <div class="search-container">
      <div class="search-box">
        <input
          type="text"
          placeholder="搜索员工姓名或部门..."
          class="search-input"
          v-model="searchKeyword"
          @keypress.enter="handleSearch"
        />
        <button class="search-button" @click="handleSearch">
          <i class="fas fa-search search-icon"></i>
          搜索
        </button>
      </div>
    </div>

    <!-- 员工学习记录表格 -->
    <div class="table-container">
      <table class="employee-table">
        <thead>
          <tr>
            <th>员工姓名</th>
            <th>部门</th>
            <th>学习状态</th>
            <th>已完成学时</th>
            <th>最后学习时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in displayedRecords" :key="index">
            <td>{{ item.name }}</td>
            <td>{{ item.assign }}</td>
            <td>
              <span :class="getStatusClass(item)">
                {{ getLearningStatus(item) }}
              </span>
            </td>
            <td>
              <div class="progress">
                <div
                  class="progress-bar"
                  :style="`width: ${getProgress(item)}%`"
                ></div>
              </div>
              <span class="progress-text">{{ getProgress(item) }}%</span>
            </td>
            <td>{{ getLastStudyTime(item) }}</td>
            <td class="action-buttons">
              <button class="view-button" @click="handleView(item)">
                查看
              </button>
              <!--            <button class="export-button" @click="handleExport(item)">导出</button>-->
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分页组件 -->
    <div class="pagination">
      <button @click="handlePage(1)" :disabled="currentPage === 1">首页</button>
      <button
        @click="handlePage(currentPage - 1)"
        :disabled="currentPage === 1"
      >
        上一页
      </button>
      <span v-for="page in pageList" :key="page">
        <button v-if="page === currentPage" class="active">{{ page }}</button>
        <button v-else @click="handlePage(page)">{{ page }}</button>
      </span>
      <button
        @click="handlePage(currentPage + 1)"
        :disabled="currentPage === totalPages"
      >
        下一页
      </button>
      <button
        @click="handlePage(totalPages)"
        :disabled="currentPage === totalPages"
      >
        末页
      </button>
    </div>
  </div>
</template>

<script>
// axios已在main.js中全局配置，使用this.$http代替直接导入的axios

export default {
  name: "TrainingRecord",
  data() {
    return {
      employeeList: [],
      filteredRecords: [],
      searchKeyword: "",
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      totalParticipants: 0,
      growthPercentage: 0,
      isPositiveGrowth: true,
    };
  },
  computed: {
    pageList() {
      const list = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, start + 4);
      for (let i = start; i <= end; i++) {
        list.push(i);
      }
      return list;
    },
    displayedRecords() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = Math.min(
        startIndex + this.pageSize,
        this.filteredRecords.length
      );
      return this.filteredRecords.slice(startIndex, endIndex);
    },
    // completedCourses() {
    //   return this.employeeList.reduce((total, employee) => {
    //     const completed = employee.courses?.filter(course => course.completionDate) || [];
    //     return total + completed.length;
    //   }, 0);
    // },
    completedCourses() {
      return this.employeeList.reduce((total, employee) => {
        const completed =
          employee.courses?.filter(
            (course) => course.videoState === "已完成"
          ) || [];
        return total + completed.length;
      }, 0);
    },
    completionRate() {
      const totalCourses = this.employeeList.reduce((total, employee) => {
        return total + (employee.courses?.length || 0);
      }, 0);
      return totalCourses > 0
        ? Math.round((this.completedCourses / totalCourses) * 100)
        : 0;
    },
    totalStudyMinutes() {
      return this.employeeList.reduce((total, employee) => {
        const employeeMinutes = employee.courses?.reduce((sum, course) => {
          return sum + (parseInt(course.actualPlayTime) || 0);
        }, 0);
        return total + employeeMinutes / 60; // 秒转换为分钟
      }, 0);
    },
  },
  methods: {
    async fetchTrainingRecords() {
      try {
        this.loading = true;
        console.log("开始请求培训记录接口...");
        console.log(
          "请求参数 - 页码:",
          this.currentPage - 1,
          "页面大小:",
          this.pageSize
        );

        // 使用this.$http代替直接导入的axios，确保token自动添加
        const response = await this.$http.get("/training/records", {
          params: {
            page: this.currentPage - 1,
            size: this.pageSize,
          },
        });

        console.log("接口响应状态:", response.status, response.statusText);
        console.log("完整响应数据:", JSON.stringify(response.data, null, 2));
        const data = response.data;

        // 处理分页信息
        this.totalPages = data.totalPages || 1;
        this.totalParticipants = data.totalElements || 0;

        // 处理原始数据，按人员重新组合
        const processedRecords = this.processTrainingRecords(
          data.content || []
        );
        this.employeeList = processedRecords;
        this.filteredRecords = processedRecords;

        // 详细记录每条数据
        if (this.employeeList.length > 0) {
          console.log("培训记录详情:");
          this.employeeList.forEach((record, index) => {
            console.log(`记录 ${index + 1}:`, JSON.stringify(record, null, 2));
          });
          this.totalParticipants = this.employeeList.length;
          console.log(
            "总人数:",
            this.totalParticipants,
            this.totalParticipants
          );
        }

        this.$message.success("培训记录加载成功");
      } catch (error) {
        console.error("获取培训记录失败:", error);
        // 401错误由main.js中的全局响应拦截器统一处理，不再在组件中重复处理
        // 仅保留通用错误提示和日志记录
        // 详细的错误信息
        if (error.response) {
          console.error("服务器响应错误 - 状态码:", error.response.status);
          console.error(
            "服务器错误数据:",
            JSON.stringify(error.response.data, null, 2)
          );
        } else if (error.request) {
          console.error("网络请求错误 - 请求未发出或未收到响应");
          console.error("请求配置:", JSON.stringify(error.config, null, 2));
        } else {
          console.error("请求配置错误:", error.message);
        }

        this.error = "获取培训记录失败，请检查网络连接";
        this.$message.error(this.error);

        this.employeeList = [];
        this.filteredRecords = [];
      } finally {
        this.loading = false;
      }
    },

    // 获取学习人数变化数据
    async fetchLearnersChange() {
      try {
        console.log("开始请求学习人数变化接口...");

        // 使用this.$http.get调用接口
        const response = await this.$http.get("/training/learners-change");

        // 输出响应信息
        console.log(
          "学习人数变化接口响应状态:",
          response.status,
          response.statusText
        );
        console.log("学习人数变化接口完整响应:", response);
        console.log("学习人数变化接口数据:", response.data);

        // 处理数据并更新到组件状态
        const data = response.data;

        // 假设接口返回的数据结构包含totalParticipants和growthPercentage
        if (data.totalParticipants !== undefined) {
          this.totalParticipants = data.totalParticipants;
        }

        if (data.percentage !== undefined) {
          const percentage = parseFloat(data.percentage);
          this.growthPercentage = Math.abs(percentage).toFixed(2);
          this.isPositiveGrowth = data.trend === "up";
        }

        console.log("处理后的学习人数变化数据:", {
          totalParticipants: this.totalParticipants,
          growthPercentage: this.growthPercentage,
          isPositiveGrowth: this.isPositiveGrowth,
        });
      } catch (error) {
        console.error("获取学习人数变化数据失败:", error);
        if (error.response) {
          console.error("服务器响应错误 - 状态码:", error.response.status);
          console.error(
            "服务器错误数据:",
            JSON.stringify(error.response.data, null, 2)
          );
        } else if (error.request) {
          console.error("网络请求错误 - 请求未发出或未收到响应");
        } else {
          console.error("请求配置错误:", error.message);
        }
      }
    },
    // getProgress(employee) {
    //   if (!employee.courses || employee.courses.length === 0) {
    //     return 0;
    //   }
    //
    //   const completedCount = employee.courses.filter(course => course.completionDate).length;
    //   return Math.round((completedCount / employee.courses.length) * 100);
    // },
    // 获取完成进度
    getProgress(employee) {
      if (!employee.courses || employee.courses.length === 0) {
        return 0;
      }

      const completedCount = employee.courses.filter(
        (course) => course.videoState === "已完成"
      ).length;

      return Math.round((completedCount / employee.courses.length) * 100);
    },
    // 获取最后学习时间
    getLastStudyTime(employee) {
      if (!employee.courses || employee.courses.length === 0) {
        return "无记录";
      }

      const completedCourses = employee.courses.filter(
        (course) => course.completionDate
      );

      if (completedCourses.length > 0) {
        // 返回最近完成的课程时间
        const latestCourse = completedCourses.reduce((latest, course) => {
          return !latest || course.completionDate > latest.completionDate
            ? course
            : latest;
        }, null);

        return latestCourse.completionDate;
      }

      // 如果没有完成课程，检查是否有进行中的课程
      const inProgressCourses = employee.courses.filter(
        (course) => course.videoState === "进行中"
      );

      if (inProgressCourses.length > 0) {
        return "学习中";
      }

      return "无记录";
    },
    // 获取状态样式类
    getStatusClass(employee) {
      const status = this.getLearningStatus(employee);
      switch (status) {
        case "已完成":
          return "status-completed";
        case "未完成":
          return "status-in-progress";
        default:
          return "status-not-started";
      }
    },
    // 搜索功能
    handleSearch() {
      console.log("执行搜索，关键词:", this.searchKeyword);

      if (!this.searchKeyword.trim()) {
        // 如果搜索关键词为空，显示所有记录
        this.filteredRecords = [...this.employeeList];
        console.log("显示所有记录:", this.filteredRecords.length);
      } else {
        const keyword = this.searchKeyword.toLowerCase().trim();
        // 使用数组的filter方法进行搜索，同时匹配姓名和部门
        this.filteredRecords = this.employeeList.filter((employee) => {
          const nameMatch = employee.name?.toLowerCase().includes(keyword);
          const assignMatch = employee.assign?.toLowerCase().includes(keyword);
          console.log(
            `员工 ${employee.name} (部门: ${employee.assign}) 匹配结果:`,
            nameMatch || assignMatch
          );
          return nameMatch || assignMatch;
        });
        console.log("过滤后记录数:", this.filteredRecords.length);
      }

      // 重置到第一页
      this.currentPage = 1;
      this.totalPages = Math.ceil(this.filteredRecords.length / this.pageSize);

      // 确保响应式更新
      this.$forceUpdate();
    },
    // 获取学习状态
    getLearningStatus(employee) {
      if (!employee.courses || employee.courses.length === 0) {
        return "未开始";
      }

      // 如果有一门课程未完成，则显示"未完成"
      const hasInProgress = employee.courses.some(
        (course) =>
          course.videoState === "进行中" || course.videoState === "未开始"
      );

      const allCompleted = employee.courses.every(
        (course) => course.videoState === "已完成"
      );

      if (allCompleted) {
        return "已完成";
      } else if (hasInProgress) {
        return "未完成";
      } else {
        return "未开始";
      }
    },
    // 处理培训记录数据，按员工分组
    processTrainingRecords(rawData) {
      const employeeMap = new Map();

      rawData.forEach((record) => {
        // 使用员工编号作为唯一标识
        const employeeKey = record.number;

        if (!employeeMap.has(employeeKey)) {
          employeeMap.set(employeeKey, {
            id: record.id,
            number: record.number,
            name: record.name,
            assign: record.assign,
            courses: [],
          });
        }

        const employee = employeeMap.get(employeeKey);

        // 添加课程信息到courses数组
        employee.courses.push({
          trainingCourse: record.trainingCourse,
          courseDescription: record.courseDescription,
          courseType: record.courseType,
          startDate: record.startDate,
          endDate: record.endDate,
          videoState: record.videoState,
          completionDate: record.completionDate,
          credits: record.credits,
          courseTotalTime: record.courseTotalTime,
          actualPlayTime: record.actualPlayTime,
        });
      });

      return Array.from(employeeMap.values());
    },

    formatEmployeeRecord(group) {
      const { records, name, number, assign } = group;

      return {
        id: records[0].id,
        number: number,
        name: name,
        assign: assign,
        courses: records.map((record) => ({
          trainingCourse: record.trainingCourse,
          courseDescription: record.courseDescription,
          courseType: record.courseType,
          startDate: record.startDate,
          endDate: record.endDate,
          videoState: record.videoState,
          completionDate: record.completionDate,
          credits: record.credits,
          courseTotalTime: record.courseTotalTime,
          totalPlayedTime: record.totalPlayedTime,
          actualPlayTime: record.actualPlayTime,
        })),
      };
    },

    // 处理查看 - 跳转到详情页面
    handleView(employee) {
      console.log("查看员工培训详情:", employee.name, employee.id);

      // 跳转到员工培训详情页面
      this.$router.push({
        name: "LearningRecordDetail",
        params: {
          number: employee.number,
        },
        query: {
          name: employee.name,
          number: employee.number,
          assign: employee.assign,
        },
      });
    },

    handleExport(item) {
      console.log("导出员工", item);
    },

    // 获取最新的完成时间
    getLatestCompletionDate(records) {
      const validDates = records
        .filter(
          (record) =>
            record.completionDate !== null &&
            record.completionDate !== undefined
        )
        .map((record) => new Date(record.completionDate))
        .filter((date) => !isNaN(date.getTime()));

      if (validDates.length === 0) {
        return null;
      }

      const latestDate = new Date(
        Math.max(...validDates.map((d) => d.getTime()))
      );
      return latestDate.toISOString().split("T")[0];
    },

    handlePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchTrainingRecords();
      }
    },
    // 初始化数据
    initData() {
      // 初始化过滤记录
      this.filteredRecords = [...this.employeeList];
      this.totalPages = Math.ceil(this.filteredRecords.length / this.pageSize);
      console.log("数据初始化完成，总记录数:", this.employeeList.length);
    },
  },
  mounted() {
    this.initData();
    this.fetchTrainingRecords();
    this.fetchLearnersChange(); // 独立调用学习人数变化接口
  },
  watch: {
    // 监听搜索关键词变化，实时搜索
    searchKeyword(newVal) {
      if (newVal === "") {
        this.handleSearch();
      }
    },
  },
};
</script>

<style scoped>
.training-record {
  font-family: "Arial", sans-serif;
  padding: 20px;
  background-color: #f5f7fa;
}

/* 统计卡片样式 */
.card-container {
  display: flex;
  justify-content: space-between;
  margin-bottom: 25px;
  gap: 20px;
}

.stat-card {
  flex: 1;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 15px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.card-content {
  text-align: center;
}

.card-content h3 {
  color: #666;
  font-size: 16px;
  margin-bottom: 10px;
}

.card-content h2 {
  color: #333;
  font-size: 24px;
  margin: 10px 0;
  font-weight: bold;
}

.growth {
  color: #1890ff;
  font-size: 14px;
}

.status {
  color: #999;
  font-size: 14px;
  display: block;
  margin-bottom: 5px;
}

.unit {
  color: #999;
  font-size: 14px;
}

/* 搜索栏样式 */
.search-container {
  margin-bottom: 20px;
}

.search-box {
  display: flex;
  width: 300px;
  align-items: center;
}

.search-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px 0 0 4px;
  outline: none;
  font-size: 14px;
  height: 32px;
  box-sizing: border-box;
}

.search-button {
  background-color: #1890ff;
  border: none;
  border-radius: 0 4px 4px 0;
  padding: 0 16px;
  cursor: pointer;
  transition: background-color 0.3s;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
}

.search-button:hover {
  background-color: #40a9ff;
}

.search-icon {
  width: 16px;
  height: 16px;
  color: white;
}

/* 表格样式 */
.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 15px;
  margin-bottom: 20px;
}

.employee-table {
  width: 100%;
  border-collapse: collapse;
}

.employee-table th,
.employee-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.employee-table th {
  background-color: #f7f7f7;
  font-weight: 600;
  color: #333;
}

.employee-table tr:hover {
  background-color: #f5f5f5;
}

/* 进度条样式 */
.progress {
  width: 100%;
  height: 8px;
  background: #e9e9e9;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: #1890ff;
  transition: width 0.5s ease;
}

/* 状态标签样式 */
.success {
  color: #4caf50;
}

.warning {
  color: #ffc107;
}

.danger {
  color: #f44336;
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  gap: 8px;
}

.view-button,
.export-button {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.view-button {
  background-color: #1890ff;
  color: white;
}

.export-button {
  background-color: #1890ff;
  color: white;
}

.view-button:hover,
.export-button:hover {
  background-color: #40a9ff;
  transform: translateY(-2px);
}

/* 分页样式 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
}

.pagination button {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.pagination button:hover:not(:disabled) {
  background-color: #f0f0f0;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination button.active {
  background-color: #1890ff;
  color: white;
  border-color: #1890ff;
}
</style>
